import React, { useEffect } from 'react';
import { Box, Typography } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import BuzzAutoCompleteSingleSelectInput from '../../../components/BuzzAutoCompleteInput/BuzzAutoCompleteSingleSelectInput';
import { getAllCurriculumFrameworks } from '../AkademySlice';

// Shared by every curriculum screen's `useSessionStorageState` call for `frameworkId`, so
// picking a framework on one screen and switching to another resumes on the same one,
// instead of each screen remembering its own independent choice.
export const CURRICULUM_FRAMEWORK_STORAGE_KEY = 'curriculum.frameworkId';

/**
 * Every curriculum v2 collection below the framework — nodes, age groups, rating scales —
 * is scoped by a required `frameworkId` query param, and suggestions are scoped by a node
 * within one. So each of those screens starts by choosing a framework rather than
 * listing everything, which is also why they show nothing until one is picked.
 */
function CurriculumFrameworkPicker({ value, onChange }) {
  const dispatch = useDispatch();

  const { curriculumFrameworks, schools } = useSelector(({ akademyReducer }) => {
    return {
      schools: akademyReducer.schools,
      curriculumFrameworks: akademyReducer.curriculumFrameworks,
    };
  });

  useEffect(() => {
    dispatch(getAllCurriculumFrameworks());
  }, []);

  // Defaults to the first framework once the list loads, so every screen below this
  // picker has something to show right away instead of sitting on the "pick a framework"
  // placeholder. Only fires while nothing is selected — it won't fight a user's choice.
  useEffect(() => {
    if (value || !(curriculumFrameworks || []).length) return;
    onChange(curriculumFrameworks[0]._id);
  }, [value, curriculumFrameworks]);

  const labelFor = ({ code, name, schoolId }) => {
    if (!schoolId) return `${code} — ${name} (Global)`;

    const school = (schools || []).find(({ _id }) => _id === schoolId);
    return `${code} — ${name} (${school ? school.name : 'School'})`;
  };

  return (
    <Box style={{ minWidth: '360px' }}>
      <Typography color="textSecondary" style={{ fontSize: '0.85rem' }}>
        Framework
      </Typography>
      <BuzzAutoCompleteSingleSelectInput
        size="small"
        placeholder="Framework"
        options={curriculumFrameworks.map((framework) => {
          return { label: labelFor(framework), value: framework._id };
        })}
        value={value}
        onChange={onChange}
      />
    </Box>
  );
}

export const FrameworkRequiredNotice = ({ children }) => (
  <Box my={4} display="flex" justifyContent="center">
    <Typography color="textSecondary">{children}</Typography>
  </Box>
);

export default CurriculumFrameworkPicker;
